import { canvasGetWebglCoordinates, getAngleDegrees, getAngleRadians, transformPointByMatrix3 } from "./common";
import { mat3 } from "gl-matrix";
import { s } from "./globalState/settings";
import { g } from "./globalState/g";
import { CommandList as cl } from "../models/CommandList";
import { a } from "./globalState/a";
import { replaceVertices } from "./webgl/reshape";
import { updateAndDrawShapes } from "./render/shapes";
import { ObjectTypes } from "./enums";
import { ElbowBlock } from "../models/resistances/ElbowBlock";
import { ConnectionPoint } from "../models/nets/ConnectionPoint";
import { Shape } from "./types";
import { Line } from "../models/shapes/Line";
import { Rectangle } from "../models/shapes/Rectangle";
import { Circle } from "../models/shapes/Circle";
import { Block } from "../models/shapes/Block";
import { Point } from "../models/Point";

export function getRotateSnap(angle_rad:number) {
  const angleDeg = Math.round(getAngleDegrees(angle_rad));
  const angle30Deg = Math.round((angleDeg / 30)) * 30;
  const angle45Deg = Math.round((angleDeg / 45)) * 45;
  const angle90Deg = Math.round((angleDeg / 90)) * 90;
  const angle30Rad = getAngleRadians(angle30Deg);
  const angle45Rad = getAngleRadians(angle45Deg);
  const angle90Rad = getAngleRadians(angle90Deg);
  let snappedAngleRad:number;

  if (Math.abs(angleDeg - angle30Deg) <= Math.abs(angleDeg - angle45Deg) && Math.abs(angleDeg - angle30Deg) <= Math.abs(angleDeg - angle90Deg)) {
    snappedAngleRad = angle30Rad;
  } else if (Math.abs(angleDeg - angle45Deg) <= Math.abs(angleDeg - angle90Deg)) {
    snappedAngleRad = angle45Rad;
  } else {
    snappedAngleRad = angle90Rad;
  }
  return snappedAngleRad;
}
export function getRotateSnap45(angle_rad:number) {
  const angleDeg = Math.round(getAngleDegrees(angle_rad));
  const angle45Deg = Math.round((angleDeg / 45)) * 45;
  const angle90Deg = Math.round((angleDeg / 90)) * 90;

  const angle45Rad = getAngleRadians(angle45Deg);
  const angle90Rad = getAngleRadians(angle90Deg);
  let snappedAngleRad:number;

  if (Math.abs(angleDeg - angle45Deg) <= Math.abs(angleDeg - angle90Deg)) {
    snappedAngleRad = angle45Rad;
  } else {
    snappedAngleRad = angle90Rad;
  }
  return snappedAngleRad;
}

export function getMoveMatrix(p1:Point, p2:Point) {
  // матрицы продолжают быть в webgl
  if (!g.context) throw new Error('WebGL context not initialized');
  const point1 = canvasGetWebglCoordinates(p1, g.context.canvas as HTMLCanvasElement);
  const point2 = canvasGetWebglCoordinates(p2, g.context.canvas as HTMLCanvasElement);
  const tx = point2.x - point1.x;
  const ty = point2.y - point1.y;

  const move_mat = mat3.fromTranslation(mat3.create(), [tx, ty]);
  mat3.transpose(move_mat, move_mat);
  return move_mat;
}

export function getRotateMatrix(c:Point, m:Point) {
  // матрицы продолжают быть в webgl
  if (!g.context) throw new Error('WebGL context not initialized');
  const center = canvasGetWebglCoordinates(c, g.context.canvas as HTMLCanvasElement);
  const mouse = canvasGetWebglCoordinates(m, g.context.canvas as HTMLCanvasElement);

  const mouse_dx = mouse.x - center.x;
  const mouse_dy = mouse.y - center.y;
  const mouse_angle_rad = Math.atan2(mouse_dy, mouse_dx);

  const angle = getRotateSnap(mouse_angle_rad);

  const translate_before_matrix = mat3.fromTranslation(mat3.create(), [-center.x, -center.y]);
  mat3.transpose(translate_before_matrix, translate_before_matrix);
  const aspect_before_matrix = mat3.fromScaling(mat3.create(), [1 / s.aspectRatio, 1]);
  const rotate_matrix = mat3.fromRotation(mat3.create(), -angle);
  const aspect_after_matrix = mat3.invert(mat3.create(), aspect_before_matrix);
  const translate_after_matrix = mat3.invert(mat3.create(), translate_before_matrix);

  let cum_matrix = mat3.create();

  mat3.multiply(cum_matrix, cum_matrix, translate_before_matrix);
  mat3.multiply(cum_matrix, cum_matrix, aspect_before_matrix);
  mat3.multiply(cum_matrix, cum_matrix, rotate_matrix);
  mat3.multiply(cum_matrix, cum_matrix, aspect_after_matrix);
  mat3.multiply(cum_matrix, cum_matrix, translate_after_matrix);
  return cum_matrix;
}

export function getRotateMatrixByAngleRad(angleRad:number, c:Point) {
  if (!g.context) throw new Error('WebGL context not initialized');
  var center = canvasGetWebglCoordinates(c, g.context.canvas as HTMLCanvasElement);

  const translate_before_matrix = mat3.fromTranslation(mat3.create(), [-center.x, -center.y]);
  mat3.transpose(translate_before_matrix, translate_before_matrix);
  const aspect_before_matrix = mat3.fromScaling(mat3.create(), [1 / s.aspectRatio, 1]);
  const rotate_matrix = mat3.fromRotation(mat3.create(), -angleRad);
  const aspect_after_matrix = mat3.invert(mat3.create(), aspect_before_matrix);
  const translate_after_matrix = mat3.invert(mat3.create(), translate_before_matrix);

  let cum_matrix = mat3.create();

  mat3.multiply(cum_matrix, cum_matrix, translate_before_matrix);
  mat3.multiply(cum_matrix, cum_matrix, aspect_before_matrix);
  mat3.multiply(cum_matrix, cum_matrix, rotate_matrix);
  mat3.multiply(cum_matrix, cum_matrix, aspect_after_matrix);
  mat3.multiply(cum_matrix, cum_matrix, translate_after_matrix);
  return cum_matrix;
}

export function getRotateMatrixFree(c:Point, m:Point) {
  // матрицы продолжают быть в webgl
  if (!g.context) throw new Error('WebGL context not initialized');
  const center = canvasGetWebglCoordinates(c, g.context.canvas as HTMLCanvasElement);
  const mouse = canvasGetWebglCoordinates(m, g.context.canvas as HTMLCanvasElement);

  const mouse_dx = mouse.x - center.x;
  const mouse_dy = mouse.y - center.y;
  const mouse_angle_rad = Math.atan2(mouse_dy, mouse_dx);

  const angle = mouse_angle_rad;
  const translate_before_matrix = mat3.fromTranslation(mat3.create(), [-center.x, -center.y]);
  mat3.transpose(translate_before_matrix, translate_before_matrix);
  const aspect_before_matrix = mat3.fromScaling(mat3.create(), [1, 1]);
  const rotate_matrix = mat3.fromRotation(mat3.create(), -angle);
  const aspect_after_matrix = mat3.invert(mat3.create(), aspect_before_matrix);
  const translate_after_matrix = mat3.invert(mat3.create(), translate_before_matrix);

  let cum_matrix = mat3.create();

  mat3.multiply(cum_matrix, cum_matrix, translate_before_matrix);
  mat3.multiply(cum_matrix, cum_matrix, aspect_before_matrix);
  mat3.multiply(cum_matrix, cum_matrix, rotate_matrix);
  mat3.multiply(cum_matrix, cum_matrix, aspect_after_matrix);
  mat3.multiply(cum_matrix, cum_matrix, translate_after_matrix);
  return cum_matrix;
}

export function getMirrorMatrix(c:Point, m:Point) {
  // матрицы продолжают быть в webgl
  if (!g.context) throw new Error('WebGL context not initialized');
  const center = canvasGetWebglCoordinates(c, g.context.canvas as HTMLCanvasElement);
  const mouse = canvasGetWebglCoordinates(m, g.context.canvas as HTMLCanvasElement);

  const mouse_dx = mouse.x - center.x;
  const mouse_dy = mouse.y - center.y;
  const mouse_angle_rad = Math.atan2(mouse_dy, mouse_dx);

  const angle = getRotateSnap(mouse_angle_rad);

  const translate_before_matrix = mat3.fromTranslation(mat3.create(), [-center.x, -center.y]);
  mat3.transpose(translate_before_matrix, translate_before_matrix);
  const aspect_before_matrix = mat3.fromScaling(mat3.create(), [1 / s.aspectRatio, 1]);
  const rotate_matrix = mat3.fromRotation(mat3.create(), -angle);
  const aspect_after_matrix = mat3.invert(mat3.create(), aspect_before_matrix);
  const translate_after_matrix = mat3.invert(mat3.create(), translate_before_matrix);

  const mirror_matrix = mat3.fromValues(
    -1, 0, 0,
    0, 1, 0,
    0, 0, 1,
  );
  let cum_matrix = mat3.create();

  mat3.multiply(cum_matrix, cum_matrix, translate_before_matrix);
  mat3.multiply(cum_matrix, cum_matrix, aspect_before_matrix);
  mat3.multiply(cum_matrix, cum_matrix, rotate_matrix);
  mat3.multiply(cum_matrix, cum_matrix, mirror_matrix); // Add the mirror matrix here
  mat3.multiply(cum_matrix, cum_matrix, aspect_after_matrix);
  mat3.multiply(cum_matrix, cum_matrix, translate_after_matrix);
  return cum_matrix;
}

export function getScaleMatrix(centerPixels:Point, distX:number, distY:number, baseDistancePixels:number, shapeDistancePixels:number) {
  const center = canvasGetWebglCoordinates(centerPixels, g.canvas);

  const ratio = baseDistancePixels / shapeDistancePixels;

  const currentDistancePixels = Math.hypot(distX, distY);

  const z = currentDistancePixels / ratio / shapeDistancePixels;

  const translate_before_matrix = mat3.fromTranslation(mat3.create(), [-center.x, -center.y]);
  mat3.transpose(translate_before_matrix, translate_before_matrix);
  const aspect_before_matrix = mat3.fromScaling(mat3.create(), [1 / s.aspectRatio, 1]);
  const scale_mat = mat3.fromScaling(mat3.create(), [z, z]);
  const aspect_after_matrix = mat3.invert(mat3.create(), aspect_before_matrix);
  const translate_after_matrix = mat3.invert(mat3.create(), translate_before_matrix);

  let cum_matrix = mat3.create();

  mat3.multiply(cum_matrix, cum_matrix, translate_before_matrix);
  mat3.multiply(cum_matrix, cum_matrix, aspect_before_matrix);
  mat3.multiply(cum_matrix, cum_matrix, scale_mat);
  mat3.multiply(cum_matrix, cum_matrix, aspect_after_matrix);
  mat3.multiply(cum_matrix, cum_matrix, translate_after_matrix);
  return cum_matrix;

}

export function transformPointByMatrixDependingOnShapeType(shape: unknown, matrix: mat3) {
  if (shape instanceof Line) {
    shape.start = transformPointByMatrix3(matrix, shape.start);
    shape.end = transformPointByMatrix3(matrix, shape.end);
    a.vertices = replaceVertices(shape, a.vertices);
  }
  else if (shape instanceof Rectangle) {
    shape.p1 = transformPointByMatrix3(matrix, shape.p1);
    shape.p2 = transformPointByMatrix3(matrix, shape.p2);
    shape.p3 = transformPointByMatrix3(matrix, shape.p3);
    shape.p4 = transformPointByMatrix3(matrix, shape.p4);

    shape.updateCenter();
  }
  else if (shape instanceof Circle) {
    shape.center = transformPointByMatrix3(matrix, shape.center);
  }
  else if (shape instanceof Block) {
    shape.shapes.forEach(s => {
      transformPointByMatrixDependingOnShapeType(s, matrix);
    });

    shape.cpoints.forEach(cp => {
      cp.point = transformPointByMatrix3(matrix, cp.point);
    });

    shape.updateSavedVertices();
  }
  else if (shape instanceof ElbowBlock) {
    shape.shapes.forEach((s: Shape) => {
      transformPointByMatrixDependingOnShapeType(s, matrix);
    });

    shape.cpoints.forEach((cp: ConnectionPoint) => {
      cp.point = transformPointByMatrix3(matrix, cp.point);
    });

    shape.center = transformPointByMatrix3(matrix, shape.center);
    shape.base = transformPointByMatrix3(matrix, shape.base);
    shape.side = transformPointByMatrix3(matrix, shape.side);

    shape.updateLinkRefSegments();
    shape.updateSavedVertices();
  }

  updateAndDrawShapes();
}
